import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Reveal } from "./Reveal";

const DATA = [
  { m: "Jan", lme: 9350, omk: 8415 },
  { m: "Feb", lme: 9480, omk: 8532 },
  { m: "Mar", lme: 9620, omk: 8658 },
  { m: "Apr", lme: 9510, omk: 8559 },
  { m: "May", lme: 9740, omk: 8766 },
  { m: "Jun", lme: 9850, omk: 8865 },
];

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-zinc-700 bg-ink px-4 py-3 font-mono text-xs">
      <p className="font-stencil text-[10px] text-copper">{label} — USD/MT</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="mt-1" style={{ color: p.color }}>
          {p.dataKey === "lme" ? "LME" : "OMKAAR"}: ${p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export const PriceChart = () => (
  <section className="border-y border-zinc-800 bg-surface" data-testid="price-section">
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 py-28 lg:grid-cols-12 lg:px-12 lg:py-40">
      <Reveal className="lg:col-span-5">
        <p className="font-mono-label text-xs text-copper">02 — The price gap</p>
        <h2 className="font-display mt-6 text-4xl font-semibold uppercase leading-tight text-zinc-100 sm:text-5xl lg:text-6xl">
          Same copper.<br /><span className="text-copper">10% less.</span>
        </h2>
        <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 md:text-base">
          We track the London Metal Exchange and price our Copperbelt ingots a flat 10% under it —
          every order, every month. No games, no hidden spreads.
        </p>

        <div className="relative mt-10 inline-block border-2 border-copper/70 p-6" data-testid="savings-box">
          <span className="absolute left-2 top-2 h-1.5 w-1.5 rounded-full bg-zinc-600" aria-hidden="true" />
          <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-zinc-600" aria-hidden="true" />
          <span className="absolute bottom-2 left-2 h-1.5 w-1.5 rounded-full bg-zinc-600" aria-hidden="true" />
          <span className="absolute bottom-2 right-2 h-1.5 w-1.5 rounded-full bg-zinc-600" aria-hidden="true" />
          <p className="font-stencil text-[10px] text-copper">You save today</p>
          <p className="font-display mt-2 text-5xl font-semibold text-zinc-100">$985<span className="text-xl text-zinc-500"> /MT</span></p>
          <p className="mt-2 font-mono text-xs text-zinc-400">≈ $98,500 saved on a 100 MT order</p>
        </div>

        <p className="mt-8 max-w-md font-mono text-[10px] leading-relaxed text-zinc-600">
          Chart shows indicative LME cash prices (USD/MT) for illustration. The Omkaar line is always 10% below —
          your final quote is confirmed live on WhatsApp.
        </p>
      </Reveal>

      <Reveal delay={0.15} className="lg:col-span-7">
        <div className="relative border border-zinc-800 bg-ink p-6 lg:p-8" data-testid="lme-chart-panel">
          {["left-2 top-2", "right-2 top-2", "left-2 bottom-2", "right-2 bottom-2"].map((p) => (
            <span key={p} className={`absolute ${p} z-10 h-1.5 w-1.5 rounded-full bg-zinc-600`} aria-hidden="true" />
          ))}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-stencil text-[10px] text-copper">LME vs Omkaar // USD per MT</p>
            <div className="flex items-center gap-5 font-mono text-[10px] text-zinc-400">
              <span className="flex items-center gap-2"><span className="h-0.5 w-5 bg-zinc-500" /> LME</span>
              <span className="flex items-center gap-2"><span className="h-0.5 w-5 bg-copper" /> Omkaar (−10%)</span>
            </div>
          </div>

          <div className="mt-6 h-[340px]" data-testid="lme-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={DATA} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="m" stroke="#52525b" tick={{ fill: "#71717a", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#27272a" }} />
                <YAxis stroke="#52525b" tick={{ fill: "#71717a", fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={false} domain={[7800, 10200]} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <Tooltip content={<ChartTip />} cursor={{ stroke: "#3f3f46" }} />
                <Line type="monotone" dataKey="lme" stroke="#71717a" strokeWidth={2} strokeDasharray="6 4" dot={{ fill: "#71717a", r: 3 }} name="LME" />
                <Line type="monotone" dataKey="omk" stroke="#d96b27" strokeWidth={3} dot={{ fill: "#d96b27", r: 4 }} activeDot={{ r: 6, fill: "#f28b4d" }} name="Omkaar" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 flex items-center justify-between gap-4 border-t border-zinc-800 pt-4">
            <div className="barcode w-32" aria-hidden="true" />
            <p className="font-mono text-[9px] tracking-widest text-zinc-600">OMK-LME-TRACK-10</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
